import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { API } from 'api';
import { PROGRESSION_MODEL_SLIDER_STEPS } from 'features/wildfires/wildfire.constants';
import {
  ProgressionModel,
  ProgressionModelDatePoint,
  ProgressionModelServer,
} from 'features/wildfires/wildfire.types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { parseProgressionModelServer } from 'features/wildfires/utils/parseProgressionModelServer';
import { generateDateRange } from './useProgressionModel.utils';
import { useProgressionModelsEnabled } from '../useProgressionModelsEnabled';

type UseProgressionModelsProps = {
  modelId: number;
};

type UseProgressionModels = Omit<
  UseQueryResult<ProgressionModel, Error>,
  'data'
> & {
  progressionModel?: ProgressionModel;
  modelDateRange: ProgressionModelDatePoint[];
};

const fetchProgressionModel = async (
  modelId: number,
): Promise<ProgressionModelServer> => {
  const response = await API.get<ProgressionModelServer>(
    `fire_progression_models/runs/${modelId}`,
  );
  return response.data;
};

export const useProgressionModel = (
  props: UseProgressionModelsProps,
): UseProgressionModels => {
  const { modelId } = props;
  const { t } = useTranslation();
  const showProgressionModels = useProgressionModelsEnabled();

  const { data: progressionModel, ...results } = useQuery({
    queryKey: ['progressionModel', modelId],
    queryFn: () => fetchProgressionModel(modelId),
    staleTime: 1000, // 1 second
    enabled: showProgressionModels,
    select: parseProgressionModelServer,
  });

  const modelDateRange = useMemo(() => {
    if (!progressionModel) return [];
    return generateDateRange({
      t,
      startDate: progressionModel.sourceReferenceTimeZero,
      endDate: progressionModel.sourceReferenceTimeEnd,
      datePoints: PROGRESSION_MODEL_SLIDER_STEPS,
    });
  }, [progressionModel, t]);

  return { ...results, progressionModel, modelDateRange };
};
